/**
 * fieldPay — FIELD-PAY-01 (Seth approved the Wrap Up & Field Payments mock,
 * 2026-09-24: "Yes proceed"). The installer collects what the customer owes on
 * an install job BEFORE the office builds the final invoice:
 *
 *   · the money is HELD against the job (allocation.job_id) and lands on the
 *     final invoice when the office closes the job (close_job v2 — the deposit's
 *     payments move there too, and the deposit invoice is voided);
 *   · collect-payment v6 does every charge/record for a job: it re-checks the
 *     amount against job_money.due server-side (never more than what's owed),
 *     refuses while financing waits on the lender, and stamps who collected;
 *   · Klarna goes through a 24-hour job pay link opened on the customer's own
 *     phone (the only QR code) — portal-pay v5 job_* + the /pay page;
 *   · cash tells the office right away (Today → Cash With the Crews) and, over
 *     the Settings amount (default $200), puts "Bring it to the office today"
 *     on the installer's Today until the office marks it received.
 *
 * Everything stays in test mode while payments_live is off.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { collectPayment } from "@/features/payments/payments";
import { useOrgSettingJson } from "@/lib/orgSettings";

/* ------------------------------------------------------------ payments */

export interface JobPayment {
  id: string;
  /** This job's share of the payment (the allocation). */
  amount: number;
  method: string | null;
  status: string;
  source: string | null;
  card_brand: string | null;
  card_last4: string | null;
  reference: string | null;
  note: string | null;
  created_at: string;
  created_by: string | null;
  collected_by_empno: string | null;
  cash_turned_in_at: string | null;
  cash_turned_in_by: string | null;
  cash_dropped_at: string | null;
  check_file_id: string | null;
  failure_reason: string | null;
  /** Still held for the job (no invoice yet) vs applied to an invoice at close. */
  held: boolean;
  /** A deposit payment that moved onto the final invoice. */
  from_deposit: boolean;
}

/** Every payment on this job: held for it, or applied to its invoices (the deposit included). */
export function useJobPayments(jobId: string | null) {
  return useQuery({
    queryKey: ["job_payments", jobId],
    enabled: !!jobId,
    staleTime: 10 * 1000,
    queryFn: async (): Promise<JobPayment[]> => {
      const { data: invs } = await supabase.from("pegasus_job_invoice").select("id, type").eq("job_id", jobId!);
      const invIds = (invs ?? []).map((i) => (i as { id: string }).id);
      // Paid on the deposit invoice (before close) or moved from it (after) = the deposit.
      const depositIds = new Set((invs ?? []).filter((i) => (i as { type?: string | null }).type === "deposit").map((i) => (i as { id: string }).id));
      const sel = "id, amount, job_id, invoice_id, applied_from_invoice_id, pegasus_payment(*)";
      const [byJob, byInv] = await Promise.all([
        supabase.from("pegasus_payment_allocation").select(sel).eq("job_id", jobId!),
        invIds.length ? supabase.from("pegasus_payment_allocation").select(sel).in("invoice_id", invIds) : Promise.resolve({ data: [], error: null }),
      ]);
      if (byJob.error) throw byJob.error;
      const seen = new Set<string>();
      const out: JobPayment[] = [];
      for (const a of [...(byJob.data ?? []), ...((byInv.data ?? []) as typeof byJob.data)] as Record<string, unknown>[]) {
        if (seen.has(String(a.id))) continue;
        seen.add(String(a.id));
        const p = a.pegasus_payment as Record<string, unknown> | null;
        if (!p) continue;
        out.push({
          id: String(p.id), amount: Number(a.amount), method: (p.method as string) ?? null, status: String(p.status),
          source: (p.source as string) ?? null, card_brand: (p.card_brand as string) ?? null, card_last4: (p.card_last4 as string) ?? null,
          reference: (p.reference as string) ?? null, note: (p.note as string) ?? null, created_at: String(p.created_at),
          created_by: (p.created_by as string) ?? null, collected_by_empno: (p.collected_by_empno as string) ?? null,
          cash_turned_in_at: (p.cash_turned_in_at as string) ?? null, cash_turned_in_by: (p.cash_turned_in_by as string) ?? null,
          cash_dropped_at: (p.cash_dropped_at as string) ?? null, check_file_id: (p.check_file_id as string) ?? null,
          failure_reason: (p.failure_reason as string) ?? null,
          held: !a.invoice_id, from_deposit: !!a.applied_from_invoice_id || (!!a.invoice_id && depositIds.has(String(a.invoice_id))),
        });
      }
      return out.sort((x, y) => y.created_at.localeCompare(x.created_at));
    },
  });
}

/** Statuses that count as money in (an in-flight card holds the balance). */
export const COUNTED = new Set(["succeeded", "partially_refunded", "recorded", "refunded", "processing"]);

export function methodLabel(p: Pick<JobPayment, "method" | "card_brand" | "card_last4" | "reference" | "source">): string {
  const brand = p.card_brand ? p.card_brand.charAt(0).toUpperCase() + p.card_brand.slice(1) : null;
  switch (p.method) {
    case "card":
    case "card_on_file":
      return [brand ?? "Card", p.card_last4 ? `•${p.card_last4}` : null].filter(Boolean).join(" ");
    case "check": return p.reference ? `Check #${p.reference}` : "Check";
    case "cash": return "Cash";
    case "klarna": return "Klarna";
    case "ach": return [brand ?? "Bank", p.card_last4 ? `•${p.card_last4}` : null].filter(Boolean).join(" ");
    case "financing": return p.reference ? `Lender · ${p.reference}` : "Lender";
    default: return p.method ? p.method.charAt(0).toUpperCase() + p.method.slice(1) : "Payment";
  }
}

function useInvalidateFieldPay(jobId: string | null) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ["job_money", jobId] });
    void qc.invalidateQueries({ queryKey: ["job_payments", jobId] });
    void qc.invalidateQueries({ queryKey: ["field_cash"] });
    void qc.invalidateQueries({ queryKey: ["payments"] });
  };
}

export interface FieldResult { ok?: boolean; payment_id?: string; status?: string; requires_action?: boolean; client_secret?: string; error?: string }

/** Check or cash, held for the job. */
export function useFieldRecord(jobId: string | null) {
  const inval = useInvalidateFieldPay(jobId);
  return useMutation({
    mutationFn: (v: { amount: number; method: "check" | "cash"; reference?: string | null; check_file_id?: string | null; note?: string | null }) =>
      collectPayment<FieldResult>({ action: "record_manual", job_id: jobId, ...v }),
    onSuccess: inval,
  });
}

/** The saved card, with the customer's OK. */
export function useFieldChargeSaved(jobId: string | null) {
  const inval = useInvalidateFieldPay(jobId);
  return useMutation({
    mutationFn: (v: { amount: number }) => collectPayment<FieldResult>({ action: "charge_saved", job_id: jobId, ...v }),
    onSuccess: inval,
    onError: inval,
  });
}

/** A card typed on the device (Stripe's own fields). A bank that asks for
 *  3-D Secure comes back requires_action — the caller finishes it with
 *  Stripe.js and then calls settle. */
export function useFieldChargeNewCard(jobId: string | null) {
  const inval = useInvalidateFieldPay(jobId);
  return useMutation({
    mutationFn: (v: { amount: number; payment_method_id: string; customer_name?: string | null; save_card?: boolean; consent_text?: string }) =>
      collectPayment<FieldResult>({ action: "charge_new_card", job_id: jobId, ...v }),
    onSuccess: inval,
    onError: inval,
  });
}

export async function settleFieldPayment(paymentId: string): Promise<FieldResult> {
  return collectPayment<FieldResult>({ action: "settle", payment_id: paymentId });
}

export interface PayLink { ok: boolean; token: string; url: string; svg: string; amount: number; expires_at: string }
/** Klarna: a 24-hour link (+ QR code) to this job's balance, for the customer's own phone. */
export function useFieldPayLink(jobId: string | null) {
  return useMutation({
    mutationFn: () => collectPayment<PayLink>({ action: "pay_link", job_id: jobId }),
  });
}

/* ----------------------------------------------- sign-off + install complete */

export function useSaveSignoff(jobId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { name: string | null; signature: string }) => {
      const { error } = await supabase.rpc("save_install_signoff", { p_job_id: jobId, p_name: v.name, p_signature: v.signature });
      if (error) throw new Error(fieldPayError(error.message));
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["job", jobId] });
      void qc.invalidateQueries({ queryKey: ["jobs"] });
    },
  });
}

export function useCrewFinish(jobId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { note?: string | null; undo?: boolean }) => {
      const { error } = await supabase.rpc("crew_finish_install", { p_job_id: jobId, p_note: v.note ?? null, p_undo: !!v.undo });
      if (error) throw new Error(fieldPayError(error.message));
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["job", jobId] });
      void qc.invalidateQueries({ queryKey: ["jobs"] });
    },
  });
}

/* ------------------------------------------------------------------ cash */

export interface FieldCash {
  payment_id: string; amount: number; collected_at: string; collected_by_empno: string | null; created_by: string | null;
  custno: string | null; job_id: string; job_title: string | null; job_custno: string | null; job_locno: string | null;
  cash_turned_in_at: string | null; cash_turned_in_by: string | null; cash_dropped_at: string | null; cash_dropped_by: string | null;
}

/** Cash collected in the field that the office hasn't marked received (optionally one installer's). */
export function useFieldCash(empno?: string | null) {
  return useQuery({
    queryKey: ["field_cash", empno ?? "all"],
    staleTime: 20 * 1000,
    refetchInterval: 60 * 1000,
    queryFn: async (): Promise<FieldCash[]> => {
      let q = supabase.from("v_field_cash").select("*").is("cash_turned_in_at", null).order("collected_at", { ascending: false });
      if (empno) q = q.eq("collected_by_empno", empno);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as FieldCash[];
    },
  });
}

export function useCashDroppedOff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { paymentId: string; undo?: boolean }) => {
      const { error } = await supabase.rpc("cash_dropped_off", { p_payment_id: v.paymentId, p_undo: !!v.undo });
      if (error) throw new Error(fieldPayError(error.message));
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["field_cash"] }); void qc.invalidateQueries({ queryKey: ["job_payments"] }); },
  });
}

export function useMarkCashReceived() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { paymentId: string; undo?: boolean }) => {
      const { error } = await supabase.rpc("mark_cash_received", { p_payment_id: v.paymentId, p_undo: !!v.undo });
      if (error) throw new Error(fieldPayError(error.message));
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["field_cash"] }); void qc.invalidateQueries({ queryKey: ["job_payments"] }); },
  });
}

/* ------------------------------------------------------------- the office */

export function useSetPayPlan(jobId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { pay_plan: "full" | "financing" | "bill_after"; plan_id?: string | null; fin_status?: string | null; approval_no?: string | null; amount?: number | null }) => {
      const { error } = await supabase.rpc("set_job_pay_plan", {
        p_job_id: jobId, p_pay_plan: v.pay_plan, p_plan_id: v.plan_id ?? null, p_fin_status: v.fin_status ?? null,
        p_approval_no: v.approval_no ?? null, p_amount: v.amount ?? null,
      });
      if (error) throw new Error(fieldPayError(error.message));
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["job_money", jobId] });
      void qc.invalidateQueries({ queryKey: ["job_payments", jobId] });
      void qc.invalidateQueries({ queryKey: ["job", jobId] });
    },
  });
}

export interface FinancingPlan { id: string; name: string; apr: number | null; term_months: number | null; plan_number: string | null; is_active: boolean }
export function useFinancingPlanList() {
  return useQuery({
    queryKey: ["financing_plans_all"],
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<FinancingPlan[]> => {
      const { data, error } = await supabase.from("pegasus_financing_plan").select("id, name, apr, term_months, plan_number, is_active").order("sort").order("name");
      if (error) throw error;
      return (data ?? []) as FinancingPlan[];
    },
  });
}

/* --------------------------------------------------------------- settings */

export interface AfterPhoto { label: string; required: boolean }
export const DEFAULT_AFTER_PHOTOS: AfterPhoto[] = [
  { label: "New furnace or air handler", required: true },
  { label: "New outdoor unit", required: true },
  { label: "Venting & gas line", required: true },
  { label: "Work area clean", required: true },
  { label: "Line set & electrical", required: false },
  { label: "Thermostat", required: false },
];
export function useAfterPhotoList() {
  return useOrgSettingJson<AfterPhoto[]>("install_after_photos", DEFAULT_AFTER_PHOTOS);
}
/** Caption an after photo carries — it's how the checklist knows which item a photo is. */
export const afterCaption = (label: string) => `After — ${label}`;

export function useFieldCashSettings() {
  const enabled = useOrgSettingJson<boolean>("field_cash_enabled", true);
  const over = useOrgSettingJson<number>("field_cash_turnin_over", 200);
  const office = useOrgSettingJson<string>("office_address", "");
  return {
    enabled: enabled.data !== false,
    turnInOver: Number.isFinite(Number(over.data)) ? Number(over.data) : 200,
    officeAddress: typeof office.data === "string" ? office.data.trim() : "",
  };
}

/* ------------------------------------------------------------------ words */

export function fieldPayError(msg: string | null | undefined): string {
  const m = String(msg ?? "");
  if (/SIGNATURE_REQUIRED/.test(m)) return "Have the customer sign in the box first.";
  if (/SIGNATURE_TOO_LARGE/.test(m)) return "That signature is too big to save — clear it and sign again.";
  if (/JOB_CLOSED/.test(m)) return "This job is already closed.";
  if (/JOB_NOT_FOUND|NOT_FOUND/.test(m)) return "Couldn't find that — refresh and try again.";
  if (/AMOUNT_REQUIRED/.test(m)) return "Enter the approved amount first.";
  if (/SIGN_IN_REQUIRED|sign in first/i.test(m)) return "Sign in again to save this.";
  if (/permission/i.test(m)) return m;
  return m || "Didn't save — check the signal and try again.";
}
